import {
	Card,
	CardBody,
	CardHeader,
	CardHeaderActions,
	CardHeading,
	IconButton,
	Id,
	Trigger,
	isSuccess,
	useEditDrawer,
	useLaravelCrudEntity,
} from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { bottomNavBarItemService } from "../shared/api/bottomNavBarService";
import { BottomNavBarItemFormValues, BottomNavBarItemSaveResource } from "../shared/types/bottomNavBarItem";
import { BottomNavBarItemCreateDrawer, BottomNavBarItemUpdateDrawer } from "./BottomNavBarItemDrawer";
import { BottomNavBarItemList } from "./BottomNavBarItemList";

const mapFormValuesToRequest = (formValues: BottomNavBarItemFormValues): BottomNavBarItemSaveResource => {
	return {
		type: formValues.type,
		visibility: formValues.visibility,
		translations: formValues.translations,
	};
};

export const BottomNavBarItemsPage: FC = () => {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const editDrawer = useEditDrawer();
	const crudOperations = useLaravelCrudEntity(
		bottomNavBarItemService,
		{
			messages: {
				create: t("Nav bar item was saved."),
				update: t("Nav bar item was saved."),
				delete: t("Nav bar item was deleted."),
			},
			entityName: t("Nav bar item"),
		},
		[bottomNavBarItemService.baseKey],
	);

	const handleReorder = useCallback(
		async (orderedIds: Id[]) => {
			const response = await bottomNavBarItemService.reorder({ orderedIds });

			if (isSuccess(response)) {
				await queryClient.invalidateQueries({ queryKey: bottomNavBarItemService.baseKey });
			}
		},
		[queryClient],
	);

	return (
		<Card>
			<CardHeader>
				<CardHeading>{t("Nav bar")}</CardHeading>
				<CardHeaderActions>
					<Trigger
						render={(drawerProps) => (
							<BottomNavBarItemCreateDrawer
								open={drawerProps.open}
								onClose={drawerProps.handleClose}
								onClosed={() => undefined}
								onSubmit={(formValues) => crudOperations.createEntity(mapFormValuesToRequest(formValues))}
							/>
						)}
					>
						{(triggerProps) => (
							<IconButton
								{...triggerProps}
								type="button"
								title={t("Create nav bar item")}
								size="small"
								color="primary"
								icon="plus"
							/>
						)}
					</Trigger>
				</CardHeaderActions>
			</CardHeader>
			<CardBody>
				<BottomNavBarItemList
					onEdit={editDrawer.handleOpen}
					onDelete={crudOperations.deleteEntity}
					onReorder={handleReorder}
				/>
				{editDrawer.editEntityId && (
					<BottomNavBarItemUpdateDrawer
						id={editDrawer.editEntityId}
						open={editDrawer.open}
						onClose={editDrawer.onClose}
						onClosed={editDrawer.onClosed}
						onSubmit={(formValues) =>
							crudOperations.updateEntity(editDrawer.editEntityId as Id, mapFormValuesToRequest(formValues))
						}
					/>
				)}
			</CardBody>
		</Card>
	);
};
